import React, { useState, useEffect, useMemo } from 'react';
import { api } from '../../api';

interface TopBotDetails {
  bot_client_id?: string;
  botclient_id?: string;
  bot_name?: string;
  botclient_name?: string;
  botclient_identifier?: string;
  total_interactions: number;
}

interface TopBotsData {
  daily: TopBotDetails[];
  weekly: TopBotDetails[];
  monthly: TopBotDetails[];
}

type SortField = 'name' | 'interactions';
type SortDirection = 'asc' | 'desc';

const TopBotsRankingPanel: React.FC = () => {
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [topBotsData, setTopBotsData] = useState<TopBotsData>({
    daily: [],
    weekly: [],
    monthly: []
  });
  const [activeTab, setActiveTab] = useState<'daily' | 'weekly' | 'monthly'>('daily');
  const [exporting, setExporting] = useState<boolean>(false);
  const [exportSuccess, setExportSuccess] = useState<boolean>(false);
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [botsPerPage, setBotsPerPage] = useState<number>(20);
  
  // Sorting states
  const [sortField, setSortField] = useState<SortField>('interactions');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  
  // Search filter
  const [searchQuery, setSearchQuery] = useState<string>('');
  
  // Option to hide bots with zero interactions
  const [hideZeroInteractions, setHideZeroInteractions] = useState<boolean>(false);

  useEffect(() => {
    const fetchTopBots = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Fetch top bots for each time period
        const [dailyResponse, weeklyResponse, monthlyResponse, allBotsResponse] = await Promise.all([
          api.getTopBots('day'),
          api.getTopBotsWeekly(),
          api.getTopBotsMonthly(),
          api.getTopBotClients() // Get all bots to ensure we have a complete list
        ]);
        
        // Process the responses
        const dailyData = Array.isArray(dailyResponse) ? dailyResponse : 
                        (dailyResponse.data || []);
        
        const weeklyData = Array.isArray(weeklyResponse) ? weeklyResponse : 
                        (weeklyResponse.data || []);
        
        const monthlyData = Array.isArray(monthlyResponse) ? monthlyResponse : 
                        (monthlyResponse.data || []);
                        
        const allBots = Array.isArray(allBotsResponse) ? allBotsResponse : 
                       (allBotsResponse.data || []);
        
        console.log('Daily bots count:', dailyData.length);
        console.log('Weekly bots count:', weeklyData.length);
        console.log('Monthly bots count:', monthlyData.length);
        console.log('All bots count:', allBots.length);
        
        // Ensure total_interactions is a number for all bots
        const processedDailyData = dailyData.map((bot: TopBotDetails) => ({
          ...bot,
          total_interactions: Number(bot.total_interactions) || 0
        }));
        
        const processedWeeklyData = weeklyData.map((bot: TopBotDetails) => ({
          ...bot,
          total_interactions: Number(bot.total_interactions) || 0
        }));
        
        const processedMonthlyData = monthlyData.map((bot: TopBotDetails) => ({
          ...bot,
          total_interactions: Number(bot.total_interactions) || 0
        }));
        
        // Para a aba diária, se não houver dados suficientes, use a lista completa de bots
        // e marque aqueles que não estão na lista diária como tendo 0 interações
        if (processedDailyData.length < 10 && allBots.length > 0) {
          console.log('Poucos ou nenhum dado diário, preenchendo com todos os bots');
          
          // Mapa para lookup rápido dos bots que já têm interações
          const dailyBotsMap = new Map();
          processedDailyData.forEach((bot: TopBotDetails) => {
            const botId = bot.bot_client_id || bot.botclient_id;
            if (botId) {
              dailyBotsMap.set(botId, bot);
            }
          });
          
          // Criar lista completa incluindo bots com zero interações
          const completeDailyData = allBots.map((bot: any) => {
            const botId = bot.id || bot.botclient_id;
            // Se o bot já está na lista diária, use esses dados
            if (botId && dailyBotsMap.has(botId)) {
              return dailyBotsMap.get(botId);
            }
            // Caso contrário, adicione como bot com zero interações
            return {
              bot_client_id: botId,
              bot_name: bot.descricao || bot.identificador || botId,
              total_interactions: 0
            };
          });
          
          setTopBotsData({
            daily: completeDailyData,
            weekly: processedWeeklyData,
            monthly: processedMonthlyData
          });
        } else {
          // Usar os dados normalmente se tiver suficientes
          setTopBotsData({
            daily: processedDailyData,
            weekly: processedWeeklyData,
            monthly: processedMonthlyData
          });
        }
      } catch (err) {
        console.error('Erro ao buscar top bots:', err);
        setError('Erro ao carregar dados de top bots');
      } finally {
        setLoading(false);
      }
    };

    fetchTopBots();
    
    // Refresh data every 5 minutes
    const interval = setInterval(fetchTopBots, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  // Reset to first page when tab changes or search query changes
  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab, searchQuery, hideZeroInteractions]);

  // Helper to format time period for display
  const formatTimePeriod = (period: 'daily' | 'weekly' | 'monthly') => {
    switch (period) {
      case 'daily': return 'Hoje';
      case 'weekly': return 'Esta Semana';
      case 'monthly': return 'Este Mês';
      default: return '';
    }
  };

  // Handle export to CSV (if needed in the future)
  const handleExportCSV = async () => {
    try {
      setExporting(true);
      // Implementation for CSV export would go here
      setExportSuccess(true);
      
      // Reset success status after 2 seconds
      setTimeout(() => {
        setExportSuccess(false);
      }, 2000);
    } catch (err) {
      console.error('Erro ao exportar CSV:', err);
    } finally {
      setExporting(false);
    }
  };

  // Get the bot name for display
  const getBotName = (bot: TopBotDetails): string => {
    return bot.bot_name || bot.botclient_name || 
      (bot.bot_client_id || bot.botclient_id || '').substring(0, 8) + '...';
  };
  
  // Get the bot ID for display (shortened)
  const getBotId = (bot: TopBotDetails): string => {
    // Preferir o identificador se disponível, senão usar o ID
    const identifier = bot.botclient_identifier;
    const id = bot.bot_client_id || bot.botclient_id || '';
    
    if (identifier && identifier !== bot.botclient_name) {
      return identifier;
    }
    
    if (!id) return '';
    // Se o ID for um UUID, mostrar apenas os primeiros 8 caracteres
    if (id.length > 20) {
      return id.substring(0, 8) + '...';
    }
    return id;
  };
  
  // Toggle sort order or change sort field
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      // Toggle sort direction if same field
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new field and default to descending for interactions, ascending for name
      setSortField(field);
      setSortDirection(field === 'interactions' ? 'desc' : 'asc');
    }
    // Reset to first page when sorting changes
    setCurrentPage(1);
  };
  
  // Toggle show/hide bots with zero interactions
  const toggleHideZeroInteractions = () => {
    setHideZeroInteractions(!hideZeroInteractions);
  };
  
  // Filter and sort the bots
  const filteredAndSortedBots = useMemo(() => {
    const rawData = topBotsData[activeTab] || [];
    
    // First filter by search query
    let filtered = searchQuery.trim() === '' 
      ? rawData 
      : rawData.filter(bot => {
          const name = getBotName(bot).toLowerCase();
          return name.includes(searchQuery.toLowerCase());
        });
    
    // Optionally filter out bots with zero interactions
    if (hideZeroInteractions) {
      filtered = filtered.filter(bot => bot.total_interactions > 0);
    }
    
    // Then sort the data
    return [...filtered].sort((a, b) => {
      if (sortField === 'name') {
        // Sort by name
        const nameA = getBotName(a).toLowerCase();
        const nameB = getBotName(b).toLowerCase();
        return sortDirection === 'asc' 
          ? nameA.localeCompare(nameB)
          : nameB.localeCompare(nameA);
      } else {
        // Sort by interactions first
        if (a.total_interactions !== b.total_interactions) {
          return sortDirection === 'asc' 
            ? a.total_interactions - b.total_interactions
            : b.total_interactions - a.total_interactions;
        }
        
        // For equal interactions, sort by name alphabetically
        const nameA = getBotName(a).toLowerCase();
        const nameB = getBotName(b).toLowerCase();
        return nameA.localeCompare(nameB);
      }
    });
  }, [topBotsData, activeTab, sortField, sortDirection, searchQuery, hideZeroInteractions]);

  // Pagination logic
  const indexOfLastBot = currentPage * botsPerPage;
  const indexOfFirstBot = indexOfLastBot - botsPerPage;
  const currentBots = filteredAndSortedBots.slice(indexOfFirstBot, indexOfLastBot);
  const totalPages = Math.ceil(filteredAndSortedBots.length / botsPerPage);

  // Change page
  const paginate = (pageNumber: number) => setCurrentPage(pageNumber);
  
  // Handle page size change
  const handlePageSizeChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setBotsPerPage(Number(event.target.value));
    setCurrentPage(1); // Reset to first page when changing page size
  };
  
  // Handle search input change
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  };

  if (loading) {
    return (
      <div className="bot-ranking-panel card">
        <h3 className="section-title with-icon">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
          </svg>
          Top Bots por Interações
        </h3>
        <div className="p-4 flex items-center justify-center">
          <div className="animate-pulse text-center">
            <div className="h-4 w-32 bg-gray-700 rounded mx-auto"></div>
            <div className="mt-2 h-3 w-24 bg-gray-700 rounded mx-auto"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bot-ranking-panel card">
        <h3 className="section-title with-icon">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
          </svg>
          Top Bots por Interações
        </h3>
        <div className="p-4 text-center text-red-400">
          <p>Erro ao carregar top bots. Tente novamente mais tarde.</p>
          {error && <p className="text-sm mt-2">{error}</p>}
        </div>
      </div>
    );
  }
  
  // Get statistics for the current tab
  const activeTabData = topBotsData[activeTab] || [];
  const botsWithInteractions = activeTabData.filter(bot => bot.total_interactions > 0).length;
  const botsWithoutInteractions = activeTabData.length - botsWithInteractions;
  
  // Calculate how many results are filtered out
  const filteredCount = filteredAndSortedBots.length;
  const totalCount = topBotsData[activeTab]?.length || 0;
  const isFiltering = searchQuery.trim() !== '' || hideZeroInteractions;
  
  return (
    <div className="bot-ranking-panel card">
      <h3 className="section-title with-icon flex items-center">
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
        </svg>
        Top Bots por Interações
      </h3>
      
      {/* Tabs for time periods */}
      <div className="tabs">
        <button 
          className={`tab ${activeTab === 'daily' ? 'active' : ''}`}
          onClick={() => setActiveTab('daily')}
        >
          Hoje
          <span className="ml-2 px-2 py-1 text-xs rounded bg-blue-800">
            {topBotsData.daily.length}
          </span>
        </button>
        <button 
          className={`tab ${activeTab === 'weekly' ? 'active' : ''}`}
          onClick={() => setActiveTab('weekly')}
        >
          Semana
          <span className="ml-2 px-2 py-1 text-xs rounded bg-indigo-800">
            {topBotsData.weekly.length}
          </span>
        </button>
        <button 
          className={`tab ${activeTab === 'monthly' ? 'active' : ''}`}
          onClick={() => setActiveTab('monthly')}
        >
          Mês
          <span className="ml-2 px-2 py-1 text-xs rounded bg-purple-800">
            {topBotsData.monthly.length}
          </span>
        </button>
        
        {/* Export CSV Button - can be implemented later if needed */}
        <button 
          className={`ml-auto export-button ${exporting ? 'exporting' : ''} ${exportSuccess ? 'success' : ''}`}
          onClick={handleExportCSV}
          disabled={exporting}
        >
          {exportSuccess ? (
            <svg 
              xmlns="http://www.w3.org/2000/svg" 
              className="h-4 w-4" 
              fill="none" 
              viewBox="0 0 24 24" 
              stroke="currentColor"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M5 13l4 4L19 7" 
              />
            </svg>
          ) : (
            <svg 
              xmlns="http://www.w3.org/2000/svg" 
              className="h-4 w-4" 
              fill="none" 
              viewBox="0 0 24 24" 
              stroke="currentColor"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M12 10v6m0 0l-3-3m3 3l3-3m-9 9h12a2 2 0 002-2V7a2 2 0 00-2-2h-3.5L10 2H4a2 2 0 00-2 2v13a2 2 0 002 2z" 
              />
            </svg>
          )}
          <span>
            {exportSuccess ? 'Exportado' : 'Exportar'}
          </span>
        </button>
      </div>
      
      {/* Statistics and filter controls */}
      <div className="bg-gray-800 p-2 mx-3 rounded mt-3 text-xs text-gray-300">
        <div className="flex items-center justify-between">
          <div>
            <span className="font-medium">{botsWithInteractions}</span> bots com interações e 
            <span className="font-medium ml-1">{botsWithoutInteractions}</span> bots sem interações
          </div>
          <label className="flex items-center cursor-pointer">
            <input 
              type="checkbox" 
              checked={hideZeroInteractions} 
              onChange={toggleHideZeroInteractions}
              className="form-checkbox h-3 w-3 text-blue-500 rounded mr-1" 
            />
            <span>Ocultar bots sem interações</span>
          </label>
        </div>
      </div>
      
      {/* Search and filter controls */}
      <div className="flex flex-col sm:flex-row justify-between items-center gap-2 my-3 px-3">
        <div className="w-full sm:w-auto relative">
          <input
            type="text"
            placeholder="Buscar bot por nome..."
            value={searchQuery}
            onChange={handleSearchChange}
            className="bg-gray-800 text-gray-200 text-sm border border-gray-700 rounded px-3 py-2 pl-9 w-full"
          />
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-4 w-4 absolute left-3 top-2.5 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-200"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          )}
        </div>
        
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-400">Itens por página:</span>
          <select 
            className="bg-gray-800 text-gray-200 text-xs border border-gray-700 rounded px-2 py-1"
            value={botsPerPage}
            onChange={handlePageSizeChange}
          >
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
        </div>
      </div>
      
      {/* Filter results message */}
      {isFiltering && (
        <div className="px-3 text-xs text-gray-400 mt-1 mb-2">
          {filteredCount === 0 ? (
            <span className="text-yellow-400">Nenhum resultado encontrado</span>
          ) : (
            <span>
              Mostrando {filteredCount} de {totalCount} bots
              {searchQuery ? ` para "${searchQuery}"` : ''}
              {hideZeroInteractions ? ' (ocultando bots sem interações)' : ''}
            </span>
          )}
        </div>
      )}
      
      {/* Table with ranking */}
      <div className="overflow-x-auto mt-1">
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="bg-gray-800">
              <th className="py-2 px-3 w-12 text-center">#</th>
              <th className="py-2 px-3 cursor-pointer select-none" onClick={() => handleSort('name')}>
                <div className="flex items-center">
                  Bot
                  {sortField === 'name' && (
                    <span className="ml-1">
                      {sortDirection === 'asc' ? '▲' : '▼'}
                    </span>
                  )}
                </div>
              </th>
              <th 
                className="py-2 px-3 text-right cursor-pointer select-none" 
                onClick={() => handleSort('interactions')}
              >
                <div className="flex items-center justify-end">
                  Interações
                  {sortField === 'interactions' && (
                    <span className="ml-1">
                      {sortDirection === 'asc' ? '▲' : '▼'}
                    </span>
                  )}
                </div>
              </th>
              <th className="py-2 px-3 w-1/4">Distribuição</th>
            </tr>
          </thead>
          <tbody>
            {filteredAndSortedBots.length === 0 ? (
              <tr>
                <td colSpan={4} className="py-4 text-center text-gray-400">
                  {isFiltering 
                    ? `Nenhum bot encontrado${searchQuery ? ` para "${searchQuery}"` : ''}`
                    : 'Nenhum dado disponível para este período'}
                </td>
              </tr>
            ) : currentBots.length === 0 ? (
              <tr>
                <td colSpan={4} className="py-4 text-center text-gray-400">
                  Carregando dados...
                </td>
              </tr>
            ) : (
              currentBots.map((bot, index) => {
                // Calculate the ranking position considering pagination
                const rankPosition = indexOfFirstBot + index + 1;
                
                // Calculate the percentage for the distribution bar relative to the max in the filtered results
                const maxInteractions = filteredAndSortedBots.find(b => b.total_interactions > 0)?.total_interactions || 1;
                const percentage = maxInteractions > 0 ? (bot.total_interactions / maxInteractions) * 100 : 0;
                
                // Determine if this is a bot with zero interactions
                const hasNoInteractions = bot.total_interactions === 0;
                
                return (
                  <tr key={index} className={`border-b border-gray-700 hover:bg-gray-750 ${hasNoInteractions ? 'opacity-70' : ''}`}>
                    <td className="py-2 px-3 text-center font-medium">
                      {rankPosition}
                    </td>
                    <td className="py-2 px-3">
                      <div className="flex flex-col">
                        <span className={`font-medium ${hasNoInteractions ? 'text-gray-400' : 'text-gray-200'}`}>
                          {getBotName(bot)}
                        </span>
                        {getBotId(bot) && (
                          <span className="text-xs text-gray-500 mt-0.5">
                            ID: {getBotId(bot)}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="py-2 px-3 text-right font-medium">
                      {new Intl.NumberFormat('pt-BR').format(
                        Number(bot.total_interactions)
                      )}
                    </td>
                    <td className="py-2 px-3">
                      {hasNoInteractions ? (
                        <div className="text-xs text-gray-500 italic">Sem interações</div>
                      ) : (
                        <div className="w-full bg-gray-700 rounded-full h-2">
                          <div 
                            className={`h-2 rounded-full ${
                              rankPosition <= 3 && !hasNoInteractions ? (
                                rankPosition === 1 ? 'bg-gradient-to-r from-blue-500 to-indigo-600' : 
                                rankPosition === 2 ? 'bg-gradient-to-r from-indigo-500 to-purple-600' :
                                'bg-gradient-to-r from-purple-500 to-pink-600'
                              ) : 'bg-gray-500'
                            }`}
                            style={{ width: `${percentage}%` }}
                          ></div>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
      
      {/* Pagination */}
      {filteredAndSortedBots.length > 0 && (
        <div className="mt-4 px-3 flex flex-col sm:flex-row justify-between items-center gap-2">
          <div className="text-xs text-gray-400 order-2 sm:order-1">
            Mostrando {indexOfFirstBot + 1}-{Math.min(indexOfLastBot, filteredAndSortedBots.length)} de {filteredAndSortedBots.length} bots
            {isFiltering && ` (filtrado de ${totalCount})`}
          </div>
          
          <div className="flex order-1 sm:order-2">
            <button 
              onClick={() => paginate(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className={`px-2 py-1 mx-1 rounded text-xs ${currentPage === 1 ? 'bg-gray-800 text-gray-500 cursor-not-allowed' : 'bg-gray-700 text-gray-200 hover:bg-gray-600'}`}
            >
              Anterior
            </button>
            
            {[...Array(Math.min(5, totalPages))].map((_, i) => {
              // Calculate page numbers to show (centered around current page)
              let pageNum: number;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (currentPage <= 3) {
                pageNum = i + 1;
              } else if (currentPage >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = currentPage - 2 + i;
              }
              
              return (
                <button 
                  key={i}
                  onClick={() => paginate(pageNum)}
                  className={`w-7 h-7 mx-1 rounded-full text-xs flex items-center justify-center ${pageNum === currentPage ? 'bg-blue-600 text-white' : 'bg-gray-700 text-gray-200 hover:bg-gray-600'}`}
                >
                  {pageNum}
                </button>
              );
            })}
            
            <button 
              onClick={() => paginate(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
              className={`px-2 py-1 mx-1 rounded text-xs ${currentPage === totalPages ? 'bg-gray-800 text-gray-500 cursor-not-allowed' : 'bg-gray-700 text-gray-200 hover:bg-gray-600'}`}
            >
              Próxima
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TopBotsRankingPanel; 